import React from 'react';
import { createRoot } from 'react-dom/client';
import PortalSwitchAccount from './App';
import type { SwitchAccountLabels } from '../../types/switch-account.d';
import eventBus, { CUSTOM_EVENTS } from '../utils/EventBus';

// Extend globalThis interface
declare global {
  // eslint-disable-next-line no-var
  var __openSwitchAccountModal: (() => void) | undefined;
}

// Parse labels from button's data attribute
function parseLabelsFromButton(button: HTMLButtonElement): SwitchAccountLabels | null {
  const dataAttr = button.dataset.modelLabels;
  if (!dataAttr) return null;

  try {
    return JSON.parse(dataAttr);
  } catch (error) {
    console.error('Failed to parse SwitchAccountLabels from data attribute:', error);
    return null;
  }
}

// Wrapper component to expose setIsOpen and accept labels
function PortalSwitchAccountWrapper({ labels }: Readonly<{ labels: SwitchAccountLabels }>) {
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    // Expose openModal function to global scope
    globalThis.__openSwitchAccountModal = () => {
      // Emit event to close mobile navigation if open
      eventBus.emit(CUSTOM_EVENTS.CLOSE_MOBILE_NAVIGATION);
      setIsOpen(true);
    };
  }, []);

  return <PortalSwitchAccount isOpen={isOpen} onOpenChange={setIsOpen} labels={labels} />;
}

// Find all switch account buttons (desktop and mobile)
const switchButtons = document.querySelectorAll<HTMLButtonElement>('[data-id="switch-account-button"]');

if (switchButtons.length > 0) {
  // Parse labels from the first button
  const firstButton = switchButtons[0];
  const labels = parseLabelsFromButton(firstButton);

  if (labels) {
    // Create a container for the React component
    const container = document.createElement('div');
    container.id = 'switch-account-modal-root';
    document.body.appendChild(container);

    const root = createRoot(container);
    root.render(<PortalSwitchAccountWrapper labels={labels} />);

    // Attach click handlers to all switch account buttons
    switchButtons.forEach(button => {
      // Prevent form submission
      const form = button.closest('form');
      if (form) {
        form.addEventListener('submit', e => {
          e.preventDefault();
          if (globalThis.__openSwitchAccountModal) {
            globalThis.__openSwitchAccountModal();
          }
        });
      }

      // Also handle direct button clicks
      button.addEventListener('click', e => {
        e.preventDefault();
        if (globalThis.__openSwitchAccountModal) {
          globalThis.__openSwitchAccountModal();
        }
      });
    });
  } else {
    console.error('Failed to initialize PortalSwitchAccount: could not parse labels from button');
  }
}
